import { ApplicationConfig, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { provideRouter, RouteReuseStrategy } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { firstValueFrom, forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { routes } from './app.routes';
import { authInterceptor } from './shared/auth/auth.interceptor';
import { AuthService } from './shared/auth/auth.service';
import { ApiOrganization, ApiOrganizationService } from './shared/services/api-organization.service';
import { OrganizationStoreService } from './shared/services/organization-store.service';
import { ApiUserResponse, ApiUserService } from './shared/services/api-user.service';
import { UserStoreService } from './shared/services/user-store.service';
import { SurveyRouteReuseStrategy } from './shared/routing/survey-route-reuse-strategy';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(withInterceptors([authInterceptor])),
    { provide: RouteReuseStrategy, useClass: SurveyRouteReuseStrategy },
    provideAppInitializer(async () => {
      const authService = inject(AuthService);
      const apiOrganizationService = inject(ApiOrganizationService);
      const organizationStore = inject(OrganizationStoreService);
      const apiUserService = inject(ApiUserService);
      const userStore = inject(UserStoreService);

      await authService.init();
      if (!authService.authenticated) return;

      const { orgs, userData } = await firstValueFrom(forkJoin({
        orgs: apiOrganizationService.getOrganizations().pipe(
          catchError(() => of<ApiOrganization[]>([]))
        ),
        userData: apiUserService.getUserData().pipe(
          catchError(() => of<ApiUserResponse | null>(null))
        ),
      }));

      if (userData) {
        userStore.setUserData(userData);
      }
      organizationStore.setOrganizations(orgs, userData?.activeOrg);
    }),
  ]
};